import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import { CHAVE_PRODUTOR } from "@/hooks/use-mercado";
import type {
  ContextoOfertaDTO,
  MensagemOfertaDTO,
  OfertaProdutorDTO,
  OfertaPublicaDTO,
  PracaCompradorDTO,
  RespostaMinhaDTO,
} from "@shared/mercado-ofertas";

/**
 * As OFERTAS, do lado do cliente.
 *
 * ⚠️ A oferta NÃO TEM PREÇO (059): não há kg pedido, saldo, valor estimado nem
 * cotação congelada para nenhum hook daqui carregar. A carga é volume estimado
 * + % de primeira; o preço é o da praça no dia da carga, e a cotação de hoje
 * viaja só como referência, com etiqueta.
 *
 * ⚠️ `fetchJson` em toda `queryFn` (ela checa `r.ok`): sem isso um 500 vira
 * "dado" e a tela quebra branca no primeiro `.map`.
 */

export const CHAVE_OFERTAS = "/api/mercado/produtor/ofertas";
export const CHAVE_PRACA_COMPRADOR = "/api/mercado/comprador/praca";
export const CHAVE_OFERTA_PUBLICA = "/api/mercado/publico/oferta";

// ─── Produtor ────────────────────────────────────────────────────────────────

export type FiltroOfertas = "abertas" | "fechadas" | "todas";

export function useOfertas(filtro: FiltroOfertas, ativo: boolean) {
  return useQuery<OfertaProdutorDTO[]>({
    queryKey: [CHAVE_OFERTAS, filtro],
    queryFn: () => fetchJson<OfertaProdutorDTO[]>(`${CHAVE_OFERTAS}?status=${filtro}`),
    enabled: ativo,
  });
}

/** UMA oferta — a tela que abre depois de publicar. */
export function useOferta(ofertaId: string | undefined) {
  return useQuery<OfertaProdutorDTO>({
    queryKey: [CHAVE_OFERTAS, "uma", ofertaId ?? ""],
    queryFn: () => fetchJson<OfertaProdutorDTO>(`${CHAVE_OFERTAS}/${ofertaId}`),
    enabled: !!ofertaId,
  });
}

/** O contexto do formulário. Recarrega ao trocar a variedade: a estimativa e o
 *  "colhido não vendido" são POR variedade. */
export function useContextoOferta(variedade: string, praca: string, ativo: boolean) {
  const params = new URLSearchParams({ variedade });
  // A praça só entra quando o produtor já escolheu uma: sem ela o servidor
  // resolve a da org, e é dessa resposta que a tela aprende qual é.
  if (praca) params.set("praca", praca);
  const qs = params.toString();
  return useQuery<ContextoOfertaDTO>({
    queryKey: [CHAVE_OFERTAS, "contexto", qs],
    queryFn: () => fetchJson<ContextoOfertaDTO>(`${CHAVE_OFERTAS}/contexto?${qs}`),
    enabled: ativo,
  });
}

/**
 * A PRÉVIA do texto da chamada — montada no servidor, com o que o formulário
 * tem agora.
 *
 * Vive no servidor porque o texto é BMP-only e formata número sem
 * `toLocaleString`: uma prévia montada aqui mostraria uma coisa e mandaria
 * outra. Praça sem cotação ainda tem texto — o que sai é a chamada sem a
 * linha de referência.
 */
export function usePreviaChamada(dados: {
  variedade: string;
  volumeKg: number;
  pctPrimeira: number;
  disponivelEm: string;
  praca?: string;
  ativo: boolean;
}) {
  const params = new URLSearchParams({
    variedade: dados.variedade,
    volumeKg: String(dados.volumeKg),
    pctPrimeira: String(dados.pctPrimeira),
    disponivelEm: dados.disponivelEm,
  });
  if (dados.praca) params.set("praca", dados.praca);
  const qs = params.toString();
  return useQuery<{ texto: string }>({
    queryKey: [CHAVE_OFERTAS, "previa", qs],
    queryFn: () => fetchJson<{ texto: string }>(`${CHAVE_OFERTAS}/previa?${qs}`),
    enabled: dados.ativo && dados.volumeKg > 0,
  });
}

export function useMensagemOferta(ofertaId: string | undefined) {
  return useQuery<MensagemOfertaDTO>({
    queryKey: [CHAVE_OFERTAS, ofertaId ?? "", "mensagem"],
    queryFn: () => fetchJson<MensagemOfertaDTO>(`${CHAVE_OFERTAS}/${ofertaId}/mensagem`),
    enabled: !!ofertaId,
  });
}

export interface NovaOfertaInput {
  variedade: "PRATA" | "NANICA";
  /** Estimado — a chamada escreve "cerca de N kg". */
  volumeKg: number;
  /** % de primeira. Na Nanica o servidor manda 100 e o texto nem cita. */
  pctPrimeira: number;
  disponivelEm: string;
  /** A praça escolhida no formulário. Ausente = a da org, resolvida no servidor. */
  praca?: string;
}

export function useCriarOferta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: NovaOfertaInput) =>
      apiRequest("POST", CHAVE_OFERTAS, dados).then(r => r.json()) as Promise<{ id: string; identificador: string; status: string }>,
    onSuccess: () => invalidarOfertas(qc),
  });
}

/**
 * Colocar na praça.
 *
 * ⚠️ Sem `confirmarVencida` e sem 409 de cotação (059). A oferta não carrega
 * preço: publicar não depende de a praça ter publicado hoje, nem de a
 * publicação estar fresca. Cotação vencida — ou praça sem cotação nenhuma —
 * só muda o que a chamada mostra como referência.
 */
export function usePublicarOferta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `${CHAVE_OFERTAS}/${id}/publicar`)
        .then(r => r.json()) as Promise<{ id: string; identificador: string; status: string; token: string | null }>,
    onSuccess: () => invalidarOfertas(qc),
  });
}

export function useAbrirDetalhes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { ofertaId: string; respostaId: string }) =>
      apiRequest("POST", `${CHAVE_OFERTAS}/${dados.ofertaId}/respostas/${dados.respostaId}/abrir`).then(r => r.json()),
    onSuccess: () => invalidarOfertas(qc),
  });
}

export function useDispensarResposta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { ofertaId: string; respostaId: string }) =>
      apiRequest("POST", `${CHAVE_OFERTAS}/${dados.ofertaId}/respostas/${dados.respostaId}/dispensar`).then(r => r.json()),
    onSuccess: () => invalidarOfertas(qc),
  });
}

/**
 * Fechar com um comprador → venda RASCUNHO no Cacho, SEM kg e SEM preço.
 *
 * ⚠️ Sem corpo: não há quanto fechar. Fechar com alguém fecha a carga — os kg
 * e o preço do dia entram na emissão do romaneio. Invalida as vendas do Cacho.
 */
export function useFecharOferta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { ofertaId: string; respostaId: string }) =>
      apiRequest("POST", `${CHAVE_OFERTAS}/${dados.ofertaId}/respostas/${dados.respostaId}/fechar`)
        .then(r => r.json()) as Promise<{ vendaId: string; status: string }>,
    onSuccess: () => {
      invalidarOfertas(qc);
      // A venda nasceu agora: a lista do Cacho e "A receber" mudaram.
      qc.invalidateQueries({ queryKey: ["/api/vendas"] });
    },
  });
}

export function useEncerrarOferta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ofertaId: string) =>
      apiRequest("POST", `${CHAVE_OFERTAS}/${ofertaId}/encerrar`).then(r => r.json()),
    onSuccess: () => invalidarOfertas(qc),
  });
}

export function useReenviarChamada() {
  return useMutation({
    mutationFn: (ofertaId: string) =>
      apiRequest("POST", `${CHAVE_OFERTAS}/${ofertaId}/reenviar`).then(r => r.json()),
  });
}

function invalidarOfertas(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [CHAVE_OFERTAS] });
  // A home do produtor mostra as ofertas em andamento e a pendência de escolha.
  qc.invalidateQueries({ queryKey: [CHAVE_PRODUTOR] });
}

// ─── Link público (`/o/:token`) ──────────────────────────────────────────────

/** `telefone` entra na query só depois que a pessoa responde — é o que traz de
 *  volta o "recebido" e o bloco do produtor quando ele abrir os detalhes. */
export function useOfertaPublica(token: string | undefined, telefone: string | null) {
  const qs = telefone ? `?telefone=${encodeURIComponent(telefone)}` : "";
  return useQuery<OfertaPublicaDTO>({
    queryKey: [CHAVE_OFERTA_PUBLICA, token ?? "", telefone ?? ""],
    queryFn: () => fetchJson<OfertaPublicaDTO>(`${CHAVE_OFERTA_PUBLICA}/${token}${qs}`),
    enabled: !!token,
    retry: false,
  });
}

/** A resposta é INTERESSE, e só: telefone e, se quiser, o nome. Sem kg. */
export function useResponderOferta(token: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { telefone: string; nome?: string | null }) =>
      apiRequest("POST", `${CHAVE_OFERTA_PUBLICA}/${token}/responder`, dados)
        .then(r => r.json()) as Promise<{ ok: true; respondida: RespostaMinhaDTO }>,
    onSuccess: () => { qc.invalidateQueries({ queryKey: [CHAVE_OFERTA_PUBLICA, token ?? ""] }); },
  });
}

/** O bloco do produtor. 404 é o estado NORMAL enquanto ele não abrir — por isso
 *  `retry: false` e nenhum toast: a ausência é a resposta. */
export function useDetalhesDoProdutor(token: string | undefined, telefone: string | null, ativo: boolean) {
  return useQuery<{ produtor: { nome: string; fazenda: string; telefone: string } | null }>({
    queryKey: [CHAVE_OFERTA_PUBLICA, token ?? "", "detalhes", telefone ?? ""],
    queryFn: () => fetchJson(`${CHAVE_OFERTA_PUBLICA}/${token}/detalhes?telefone=${encodeURIComponent(telefone ?? "")}`),
    enabled: ativo && !!token && !!telefone,
    retry: false,
  });
}

// ─── Tela Praça (comprador logado) ───────────────────────────────────────────

export function usePracaComprador(filtro: { praca?: string; variedade?: string }, ativo: boolean) {
  const params = new URLSearchParams();
  if (filtro.praca) params.set("praca", filtro.praca);
  if (filtro.variedade) params.set("variedade", filtro.variedade);
  const qs = params.toString();
  return useQuery<PracaCompradorDTO>({
    queryKey: [CHAVE_PRACA_COMPRADOR, filtro.praca ?? "", filtro.variedade ?? ""],
    queryFn: () => fetchJson<PracaCompradorDTO>(`${CHAVE_PRACA_COMPRADOR}${qs ? `?${qs}` : ""}`),
    enabled: ativo,
  });
}

/** "Tenho interesse" na tela Praça — o telefone é o da sessão, nada vai no corpo. */
export function useResponderNaPraca() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ofertaId: string) =>
      apiRequest("POST", `${CHAVE_PRACA_COMPRADOR}/${ofertaId}/responder`).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_PRACA_COMPRADOR] });
      qc.invalidateQueries({ queryKey: ["/api/mercado/comprador/home"] });
    },
  });
}

// LÁPIDE: `useDefinirMinhasPracas` foi para `use-cotacoes.ts`. "Minhas praças" é
// da ORGANIZAÇÃO, não das Ofertas — ficou aqui por ter nascido na tela Praça, e
// com o desligamento (`OFERTAS_ATIVAS`) teria levado junto o filtro das Cotações
// do comprador, que é a tela que sobrou. A rota é a mesma.
